import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Gavel, Headphones, Instagram, Link as LinkIcon, Mail,
  MessageCircle, Send, Trophy,
} from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import { renderSettingText } from '../../lib/textTemplate';
import { brandName } from '../../lib/brand';
import { resolveApiAssetUrl } from '../../lib/api';
import SponsorshipStrip from '../../components/SponsorshipStrip';
import EntryDomainStrip from '../../components/EntryDomainStrip';

// Panelde her menü grubuna bir ikon seçilir (FooterManagement'taki liste ile
// aynı anahtarlar); tanınmayan değer bağlantı ikonuna düşer.
const GROUP_ICONS = {
  'file-text': FileText,
  gavel: Gavel,
  headphones: Headphones,
  trophy: Trophy,
  link: LinkIcon,
};

/**
 * Paris şemasının footer'ı.
 *
 * İçerik yönetim panelindeki `footer_*` kayıtlarından gelir; bu şema referans
 * arayüzdeki sırayı kurar: **beş sütunlu** menü ızgarası → lisans metni →
 * lisans/sponsor rozetleri → "YATIRIM YÖNTEMLERİ" şeridi → 18+ ve telif satırı.
 * Sütun sayısı sabittir çünkü referansta beş başlık vardır; panelde daha az
 * grup tanımlıysa ızgara kendiliğinden daralır.
 */
const ParisFooter = () => {
  const settings = useSettings();
  const menuGroups = settings.footer_menu_groups || [];
  const licenses = (settings.footer_licenses || []).filter((item) => item.image_url);
  // Logosu ve etiketi birden boş olan yatırım yöntemi çizilmez.
  const paymentMethods = (settings.footer_payment_methods || [])
    .filter((item) => item.image_url || item.label);
  const sponsors = (settings.footer_sponsors || []).filter((item) => item.image_url);

  const socials = [
    { url: settings.telegram_url, icon: Send, label: 'Telegram' },
    { url: settings.whatsapp_url, icon: MessageCircle, label: 'WhatsApp' },
    { url: settings.instagram_url, icon: Instagram, label: 'Instagram' },
    { url: settings.contact_email ? `mailto:${settings.contact_email}` : '', icon: Mail, label: 'E-posta' },
  ].filter((item) => item.url);

  // Telif satırı referansta "2021 - 2026 Parisbahis" biçimindedir. Başlangıç
  // yılı panelden gelmiyorsa yalnızca içinde bulunulan yıl yazılır; uydurma bir
  // kuruluş yılı yazmak yazılımı satın alan her kurulumda yanlış olurdu.
  const brand = brandName(settings);
  const thisYear = new Date().getFullYear();
  const startYear = Number(settings.footer_start_year) || 0;
  const yearLabel = startYear && startYear < thisYear
    ? `${startYear} - ${thisYear}`
    : String(thisYear);

  return (
    <footer className="paris-footer">
      <div className="paris-footer-inner">
        {menuGroups.length > 0 && (
          <div className="paris-footer-cols">
            {menuGroups.map((group, groupIndex) => {
              const GroupIcon = GROUP_ICONS[group.icon] || LinkIcon;
              return (
                <section key={`${group.title}-${groupIndex}`}>
                  <h2 className="paris-footer-title">
                    <GroupIcon size={13} aria-hidden="true" />
                    {renderSettingText(group.title, settings)}
                  </h2>
                  {(group.links || []).map((item, itemIndex) => (
                    (item.url || '').startsWith('/') && !item.new_tab ? (
                      <Link
                        key={`${item.label}-${itemIndex}`}
                        to={item.url}
                        className="paris-footer-link"
                      >
                        {renderSettingText(item.label, settings)}
                      </Link>
                    ) : (
                      <a
                        key={`${item.label}-${itemIndex}`}
                        href={item.url || '#'}
                        target={item.new_tab ? '_blank' : undefined}
                        rel={item.new_tab ? 'noreferrer' : undefined}
                        className="paris-footer-link"
                      >
                        {renderSettingText(item.label, settings)}
                      </a>
                    )
                  ))}
                </section>
              );
            })}
          </div>
        )}

        {socials.length > 0 && (
          <div className="paris-footer-social">
            {socials.map((social) => (
              <a
                key={social.label}
                href={social.url}
                target="_blank"
                rel="noreferrer"
                aria-label={social.label}
              >
                <social.icon size={16} />
              </a>
            ))}
          </div>
        )}

        {settings.footer_text && (
          <div className="paris-footer-seo">
            <p>{renderSettingText(settings.footer_text, settings)}</p>
            {settings.contact_phone && (
              <p>
                Sorularınız için <strong>{settings.contact_phone}</strong> numarasından
                bize ulaşabilir, önerilerinizi iletebilirsiniz.
              </p>
            )}
          </div>
        )}

        {settings.footer_license_text && (
          <p className="paris-footer-license-text">
            {renderSettingText(settings.footer_license_text, settings)}
          </p>
        )}

        {(licenses.length > 0 || sponsors.length > 0) && (
          <div className="paris-footer-badges">
            {licenses.map((license, index) => {
              const label = renderSettingText(license.name || 'Lisans', settings);
              const image = (
                <img src={resolveApiAssetUrl(license.image_url)} alt={label} loading="lazy" />
              );
              return license.link ? (
                <a
                  key={`${license.name}-${index}`}
                  href={license.link}
                  target={license.new_tab ? '_blank' : undefined}
                  rel={license.new_tab ? 'noreferrer' : undefined}
                  className="paris-footer-license"
                  aria-label={label}
                >
                  {image}
                </a>
              ) : (
                <span key={`${license.name}-${index}`} className="paris-footer-license">
                  {image}
                </span>
              );
            })}
            {sponsors.map((sponsor, index) => {
              const label = renderSettingText(sponsor.name || 'Sponsor', settings);
              const image = (
                <img src={resolveApiAssetUrl(sponsor.image_url)} alt={label} loading="lazy" />
              );
              return sponsor.link ? (
                <a
                  key={`${sponsor.name}-${index}`}
                  href={sponsor.link}
                  target={sponsor.new_tab ? '_blank' : undefined}
                  rel={sponsor.new_tab ? 'noreferrer' : undefined}
                  className="paris-footer-sponsor"
                  aria-label={label}
                >
                  {image}
                </a>
              ) : (
                <span key={`${sponsor.name}-${index}`} className="paris-footer-sponsor">
                  {image}
                </span>
              );
            })}
          </div>
        )}

        {paymentMethods.length > 0 && (
          <div className="paris-footer-pay">
            <h2 className="paris-footer-pay-title">
              {settings.footer_payment_title
                ? renderSettingText(settings.footer_payment_title, settings)
                : 'YATIRIM YÖNTEMLERİ'}
            </h2>
            <div className="paris-footer-pay-list">
              {paymentMethods.map((method, index) => {
                const label = renderSettingText(method.label || '', settings);
                const content = (
                  <>
                    {method.image_url && (
                      <img
                        src={resolveApiAssetUrl(method.image_url)}
                        alt={label || 'Yatırım yöntemi'}
                        loading="lazy"
                      />
                    )}
                    {label && <span className="paris-footer-pay-label">{label}</span>}
                  </>
                );
                const key = `${method.label}-${method.image_url}-${index}`;
                return method.link ? (
                  <a
                    key={key}
                    href={method.link}
                    target={method.new_tab ? '_blank' : undefined}
                    rel={method.new_tab ? 'noreferrer' : undefined}
                    className="paris-footer-pay-item"
                  >
                    {content}
                  </a>
                ) : (
                  <span key={key} className="paris-footer-pay-item">{content}</span>
                );
              })}
            </div>
          </div>
        )}

        <div className="paris-footer-bottom">
          <span className="paris-footer-age" aria-label="18 yaş sınırı">18+</span>
          <span className="paris-footer-copy">
            {brand ? `${yearLabel} ${brand}` : yearLabel}
          </span>
        </div>
      </div>
      {/* Sponsorluk bandı footer'ın en altındadır ve beş kabukta da
          aynı bileşendir; kayıt yoksa hiçbir şey çizmez. */}
      <EntryDomainStrip />
      <SponsorshipStrip />
    </footer>
  );
};

export default ParisFooter;
